import { toast } from 'react-toastify'

import DI from '../../app/di'
import { Creative, CreativeAction } from '../../interfaces/entities/creative'
import { ICreativeService } from '../../interfaces/services/creatives'
import { IProjectService } from '../../interfaces/services/projects'
import { translateWithUserLocale } from '../../utils/i18n'
import { on } from '../listener'
import * as actions from './actions'

export const commands = {
  goToCreative: (newCreative: Creative, creativeAction: CreativeAction) => {
    if (creativeAction.name === 'duplicate') {
      const history = DI.get('history')
      history.push(`/creatives/${newCreative.id}`)
    }
  },
}

const projectService = () => DI.get('ProjectService') as IProjectService
const creativeService = () => DI.get('CreativeService') as ICreativeService

on(actions.getProject, async (action, api) => {
  api.dispatch(
    actions.getProjectComplete(
      await projectService().getProject(action.payload),
    ),
  )
})

on(actions.doCreativeAction, async (action, api) => {
  api.dispatch(
    actions.doCreativeActionComplete(
      await creativeService().doCreativeAction(
        action.payload.action,
        action.payload.creative,
      ),
    ),
  )
})

on(actions.doCreativeActionComplete, (action, api) => {
  // The project the reducer was called with, i.e. the one the command
  // arguments used to close over.
  const project = api.getOriginalState().project.project

  if (action.payload.type !== 'success') {
    toast.error(action.payload.payload.message, { autoClose: false })
    return
  }

  const newCreative = action.payload.payload.creative
  const creativeAction = action.payload.payload.action

  toast.success(action.payload.payload.message)

  if (project == null) {
    return
  }

  if (creativeAction.name === 'duplicate') {
    commands.goToCreative(newCreative, creativeAction)
  } else {
    api.dispatch(actions.getProject(project.id))
  }
})

on(actions.doProjectAction, async (action, api) => {
  api.dispatch(
    actions.doProjectActionComplete(
      await projectService().doProjectAction(
        action.payload.action,
        action.payload.project,
      ),
    ),
  )
})

on(actions.doProjectActionComplete, async (action, api) => {
  const project = api.getOriginalState().project.project

  if (action.payload.type !== 'success') {
    toast.error(action.payload.payload.message, { autoClose: false })
    return
  }

  const newProject = action.payload.payload

  toast.success(
    newProject.message
      ? newProject.message
      : translateWithUserLocale('success_message.change_status'),
  )

  // Reloads the project without going through `getProject`, so `loading`
  // stays false — same as the command this replaces.
  api.dispatch(
    actions.getProjectComplete(
      await projectService().getProject(
        project != null ? project.id : newProject.id,
      ),
    ),
  )
})

on(actions.saveProject, async (action, api) => {
  api.dispatch(
    // `saveProject` resolves to `{ project, created }` rather than a Project;
    // the cast preserves the behavior redux-loop's `successActionCreator` had.
    actions.saveProjectCompleted(
      (await projectService().saveProject(action.payload)) as any,
    ),
  )
})

on(actions.setProjectComment, async (action, api) => {
  api.dispatch(
    // `setProjectComment` resolves to a Comment rather than a Project; the
    // cast preserves the behavior redux-loop's `successActionCreator` had.
    actions.saveProjectCompleted(
      (await projectService().setProjectComment(
        action.payload.projectId,
        action.payload.comment,
      )) as any,
    ),
  )
})

on(actions.removeFormatSet, async (action, api) => {
  api.dispatch(
    // `removeFormatset` resolves with a narrower error type than
    // `saveProjectCompleted` accepts; cast preserves the previous behavior.
    actions.saveProjectCompleted(
      (await projectService().removeFormatset(action.payload)) as any,
    ),
  )
})

on(actions.saveProjectCompleted, (_action, api) => {
  const project = api.getOriginalState().project.project

  if (project !== null) {
    api.dispatch(actions.getProject(project.id))
  }
})

on(actions.getPlatforms, async (_action, api) => {
  api.dispatch(
    actions.getPlatformsComplete(await projectService().getPlatforms()),
  )
})
